import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import api from '../api/axiosConfig';

/**
 * 알림을 눌렀을 때 갈 곳. 서버가 path 를 채워 보내지만, 예전 형식으로 오는
 * 알림도 있을 수 있어 id 로도 만들어낸다.
 */
const resolvePath = (data = {}) => {
    if (data.path) return data.path;
    if (data.reportId) return `/reports/${data.reportId}`;
    if (data.issueId) return `/issues/${data.issueId}`;
    return null;
};

/**
 * 알림 탭은 Router 밖에서 들어온다. 여기서 곧장 window.location 을 건드리면
 * SPA 가 통째로 다시 뜨면서, 하던 점검이 있으면 그대로 날아간다. 그래서 갈 곳만
 * 적어 두고 Router 안의 다리(PushNavigationBridge)가 옮겨 주도록 한다.
 * 앱이 꺼져 있다가 알림으로 열리는 경우엔 다리가 아직 없으므로, 값은 누가
 * 가져갈 때까지 남겨 둔다.
 */
let pendingPath = null;
const PUSH_NAVIGATE_EVENT = 'push-navigate';

const PushNotificationService = {
    init: async () => {
        if (!Capacitor.isNativePlatform()) {
            console.log('Push notifications not supported on web');
            return;
        }

        try {
            // 1. Create the default notification channel (CRITICAL for Android 8+)
            // This ensures the app appears in the system notification settings
            if (Capacitor.getPlatform() === 'android') {
                // A channel's importance is fixed once Android has created it, so
                // raising it means publishing a new channel rather than editing
                // the old one. 'default' was importance 3, which only lands in
                // the shade; an inspection alert needs to interrupt, so this one
                // is 4 (HIGH) and pops up.
                await PushNotifications.createChannel({
                    id: 'alerts_v2',
                    name: '점검 알림',
                    description: '점검 및 이슈 알림입니다.',
                    importance: 4, // HIGH - shows as a heads-up popup
                    visibility: 1, // public
                    vibration: true,
                });

                // Retire the old one so it stops appearing in system settings as
                // a channel that never does anything.
                try {
                    await PushNotifications.deleteChannel({ id: 'default' });
                } catch (e) {
                    // Absent on a fresh install; nothing to clean up.
                }
                console.log('Notification channel created: alerts_v2');
            }

            // 2. Request permission
            let permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }

            if (permStatus.receive !== 'granted') {
                console.log('User denied permissions!');
                return;
            }

            // 3. Register with FCM/APNS
            await PushNotifications.register();
            console.log('Push notification registration initiated');
        } catch (error) {
            console.error('Push notification initialization error:', error);
        }

        // Listeners (only add if not already added or rely on Capacitor's internal handling)
        PushNotifications.removeAllListeners(); // Clean up to avoid duplicate listeners
        PushNotifications.addListener('registration', async (token) => {
            console.log('Push registration success, token: ' + token.value);
            // Cache the token locally so we can send it later if the user is not logged in yet
            localStorage.setItem('fcmToken', token.value);
            try {
                // Only send to backend if the user is already logged in
                if (localStorage.getItem('token')) {
                    await api.post('/notifications/token', {
                        token: token.value,
                        platform: Capacitor.getPlatform()
                    });
                    console.log('Push token sent to server.');
                }
            } catch (error) {
                console.error('Error sending token to server:', error);
            }
        });

        PushNotifications.addListener('registrationError', (error) => {
            console.error('Error on registration: ' + JSON.stringify(error));
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('Push received: ' + JSON.stringify(notification));
            // You can implement custom logic here (e.g., showing a local modal if app is open)
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            const path = resolvePath(notification.notification?.data);
            if (!path) return;
            pendingPath = path;
            window.dispatchEvent(new CustomEvent(PUSH_NAVIGATE_EVENT));
        });
    },

    /**
     * 알림함에 쌓인 것을 걷어낸다.
     *
     * 런처의 빨간 풍선은 앱이 세는 값이 아니라 **안드로이드 알림함에 남아 있는
     * 알림 개수**다. 앱에서 읽음 표시를 해도 알림함의 사본은 그대로라, 다 읽고
     * 나서도 아이콘에 숫자가 남아 있었다.
     */
    clearDelivered: async () => {
        if (!Capacitor.isNativePlatform()) return;
        try {
            await PushNotifications.removeAllDeliveredNotifications();
        } catch (e) {
            // 지우지 못해도 앱이 하던 일을 멈출 이유는 없다.
            console.error('Failed to clear delivered notifications', e);
        }
    },

    /** Router 안의 다리가 가져간다. 한 번 가져가면 비운다. */
    takePendingPath: () => {
        const path = pendingPath;
        pendingPath = null;
        return path;
    },

    NAVIGATE_EVENT: PUSH_NAVIGATE_EVENT,

    sendStoredTokenToServer: async () => {
        const token = localStorage.getItem('fcmToken');
        // Only attempt to send if we have a push token and an auth token
        if (token && localStorage.getItem('token')) {
            try {
                await api.post('/notifications/token', {
                    token: token,
                    platform: Capacitor.getPlatform()
                });
                console.log('Stored push token sent to server.');
            } catch (error) {
                console.error('Error sending stored token to server:', error);
            }
        }
    }
};

export default PushNotificationService;
